import { useEffect, useState } from 'react';
import { subscribeToApiActivity } from '../../lib/api';

/**
 * A thin, fixed progress bar pinned to the very top of the viewport that
 * appears whenever one or more API requests are in flight and disappears once
 * they all settle. It gives a single, always-visible signal that "something is
 * being fetched" without wiring a loading flag into every call site — every
 * request through the shared axios client is counted automatically.
 *
 * Mounted once at the app root. Purely presentational; it reads from the
 * `subscribeToApiActivity` pub/sub exposed by the API client.
 */
export default function GlobalLoadingBar() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    // A short trailing delay before hiding avoids a flicker when requests fire
    // back-to-back (one settles a moment before the next starts).
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    const unsubscribe = subscribeToApiActivity((count) => {
      if (count > 0) {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = undefined;
        }
        setActive(true);
      } else {
        hideTimer = setTimeout(() => setActive(false), 250);
      }
    });

    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      unsubscribe();
    };
  }, []);

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] h-0.5 pointer-events-none transition-opacity duration-200 ${
        active ? 'opacity-100' : 'opacity-0'
      }`}
      role="progressbar"
      aria-hidden={!active}
      aria-label="Loading"
    >
      <div className="h-full w-full overflow-hidden bg-secondary-100">
        <div className="h-full w-2/5 rounded-full bg-secondary-500 animate-global-loading-bar" />
      </div>
    </div>
  );
}
